import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Upload, Newspaper } from "lucide-react";
import { toast } from "sonner";
import api, { formatError, fileUrl } from "@/lib/api";
import { StatusBadge } from "@/pages/OrdersPage";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const EMPTY = { title: "", body: "", tags: "", keywords: "", cover_image: "", attachment_url: "", attachment_name: "", status: "DRAFT" };

function PostEditor({ post, onDone }) {
  const isEdit = !!post?.id;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    if (open) {
      setF(post ? { ...post, tags: (post.tags || []).join(", "), keywords: (post.keywords || []).join(", ") } : EMPTY);
    }
  }, [open, post]);

  const upload = async (file, target) => {
    if (!file) return;
    setUploading(target);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      if (target === "cover") setF((p) => ({ ...p, cover_image: data.url }));
      else setF((p) => ({ ...p, attachment_url: data.url, attachment_name: data.name }));
      toast.success(data.optimized ? `Uploaded & optimized (${Math.round(data.size / 1024)}KB)` : "File uploaded");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setUploading("");
    }
  };

  const save = async () => {
    const payload = {
      ...f,
      tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean),
      keywords: f.keywords.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (isEdit) await api.put(`/admin/posts/${post.id}`, payload);
      else await api.post("/admin/posts", payload);
      toast.success(isEdit ? "Post updated" : "Post created");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button data-testid={`edit-post-${post.id}`} variant="outline" size="sm" className="rounded-md"><Pencil className="w-3.5 h-3.5" /></Button>
        ) : (
          <Button data-testid="new-post-btn" className="rounded-md bg-slate-900"><Plus className="w-4 h-4 mr-1.5" /> New post</Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="post-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Post title"
            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
          <textarea data-testid="post-body" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Write the post content…" rows={8}
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500" />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="post-tags" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} placeholder="Tags (comma separated: offers, festival)"
              className="h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
            <input data-testid="post-keywords" value={f.keywords} onChange={(e) => setF({ ...f, keywords: e.target.value })} placeholder="Keywords (search terms)"
              className="h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <input data-testid="post-cover" value={f.cover_image} onChange={(e) => setF({ ...f, cover_image: e.target.value })} placeholder="Cover image URL (or upload →)"
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500" />
              <label className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-blue-600 cursor-pointer">
                <Upload className="w-3.5 h-3.5" /> {uploading === "cover" ? "Uploading…" : "Upload cover image"}
                <input data-testid="post-cover-upload" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0], "cover")} />
              </label>
              <p className="text-[10px] text-slate-400 mt-1">Large images auto-optimized · up to 10MB</p>
              {f.cover_image && <img src={fileUrl(f.cover_image)} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">Attachment (PDF, image, doc)</p>
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-blue-600 cursor-pointer">
                <Upload className="w-3.5 h-3.5" /> {uploading === "attachment" ? "Uploading…" : "Upload attachment"}
                <input data-testid="post-attachment-upload" type="file" accept="image/*,.pdf,.txt,.csv" className="hidden" onChange={(e) => upload(e.target.files[0], "attachment")} />
              </label>
              {f.attachment_name && <p data-testid="post-attachment-name" className="text-xs text-slate-600 mt-2 font-mono">{f.attachment_name}</p>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select data-testid="post-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
              className="h-10 rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 bg-white">
              <option value="DRAFT">Draft (hidden)</option>
              <option value="PUBLISHED">Published (live on site)</option>
            </select>
            <Button data-testid="post-save" onClick={save} disabled={!f.title} className="rounded-md bg-slate-900">{isEdit ? "Save changes" : "Create post"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPosts() {
  const [posts, setPosts] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/posts").then(({ data }) => setPosts(data)).catch((e) => toast.error(formatError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/posts/${id}`);
      toast.success("Post deleted");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <div data-testid="admin-posts">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500 flex items-center gap-2"><Newspaper className="w-4 h-4" /> Published posts appear at /blog and on the homepage.</p>
        <PostEditor onDone={load} />
      </div>
      <div className="border border-slate-100 rounded-xl overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Post</TableHead><TableHead>Tags</TableHead><TableHead>Status</TableHead><TableHead>Views</TableHead><TableHead>Date</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {posts.map((p) => (
              <TableRow key={p.id} data-testid={`post-row-${p.id}`}>
                <TableCell><p className="font-semibold text-sm line-clamp-1 max-w-[280px]">{p.title}</p><p className="text-[11px] text-slate-400">{p.author}</p></TableCell>
                <TableCell><div className="flex gap-1 flex-wrap">{(p.tags || []).map((t) => <span key={t} className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-semibold">{t}</span>)}</div></TableCell>
                <TableCell><StatusBadge status={p.status} /></TableCell>
                <TableCell>{p.views || 0}</TableCell>
                <TableCell className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString("en-IN")}</TableCell>
                <TableCell><div className="flex gap-2"><PostEditor post={p} onDone={load} /><Button data-testid={`delete-post-${p.id}`} variant="outline" size="sm" className="rounded-md text-red-500" onClick={() => remove(p.id)}><Trash2 className="w-3.5 h-3.5" /></Button></div></TableCell>
              </TableRow>
            ))}
            {!posts.length && <TableRow><TableCell colSpan={6} className="text-center py-16 text-slate-400 text-sm">No posts yet. Create your first blog post.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
